/**
 * Utility functions for the business model queue strategy
 */

import { fetchQueries } from '../../api';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../../discoveryQueueSchema';

// Clean confirmation message for session restoration
export const restore_message = "I reviewed your existing workforce, and this is the distribution across different business models. Which business model(s) would you like to target?";

/**
 * Generate business model data and populate centralized queue
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Current decision object
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Object>} The business model data
 */
export const generateBusinessModelData = async (messages, setMessages, decision, useCache = true) => {
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating business model data and populating centralized queue...`
            }
          : msg
      );
      return updatedMessages;
    });
  }

  try {
    console.log('🏢 BUSINESS MODEL: Starting data generation and queue population');

    // Fetch queries dictionary
    const queries = await fetchQueries();
    if (!queries || Object.keys(queries).length === 0) {
      throw new Error('Failed to fetch queries dictionary');
    }

    console.log('🏢 BUSINESS MODEL: Fetched queries:', Object.keys(queries).length);

    // Group queries by business model time periods
    const recentBusinessModelQueries = [];
    const currentBusinessModelQueries = [];
    const pastBusinessModelQueries = [];
    const processedQueryIds = new Set(); // Track processed queries to avoid duplicates

    Object.keys(queries).forEach((queryId) => {
      let queryData = queries[queryId];
      queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
      
      if (queryData.macro_taxonomy.length === 0 || processedQueryIds.has(queryId)) {
        return;
      }
      
      // Check if any macro_taxonomy contains "Recent Company;Business Model"
      const hasRecentBusinessModel = queryData.macro_taxonomy.some(tax => 
        tax.includes('Recent') && tax.includes('Business Model')
      );
      
      // Check if any macro_taxonomy contains "Current Company;Business Model"
      const hasCurrentBusinessModel = queryData.macro_taxonomy.some(tax => 
        tax.includes('Current') && tax.includes('Business Model')
      );
      
      // Check if any macro_taxonomy contains "Past Company;Business Model"
      const hasPastBusinessModel = queryData.macro_taxonomy.some(tax => 
        tax.includes('Past') && tax.includes('Business Model')
      );

      if (hasRecentBusinessModel) {
        recentBusinessModelQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`🏢 BUSINESS MODEL: Added to RECENT: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      } else if (hasCurrentBusinessModel) {
        currentBusinessModelQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`🏢 BUSINESS MODEL: Added to CURRENT: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      } else if (hasPastBusinessModel) {
        pastBusinessModelQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`🏢 BUSINESS MODEL: Added to PAST: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      }
    });

    console.log('🏢 BUSINESS MODEL: Grouped queries:', {
      recent: recentBusinessModelQueries.length,
      current: currentBusinessModelQueries.length,
      past: pastBusinessModelQueries.length
    });

    // Create queue items for each time period
    const queueItems = [];

    // 1. Recent Business Model queue item
    if (recentBusinessModelQueries.length > 0) {
      const recentBoolStr = `((${recentBusinessModelQueries.map(q => q.id).join(' OR ')}))`;
      const recentQueueItem = {
        queue_id: `business_model_recent_${Date.now()}`,
        section_id: 'business_model_recent',
        section_name: 'Recent Company Business Model',
        section_type: 'Must Have',
        section_order: 1,
        topic: 'business_model',
        bool_str: recentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: 'I reviewed your existing workforce, and this is the distribution across different recent company business models. Which business model(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Recent Company Business Models',
          chart_subtitle: 'Distribution of recent company business models in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: true, // Only show items with non-zero percentages
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: recentBusinessModelQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(recentQueueItem);
    }

    // 2. Current Business Model queue item
    if (currentBusinessModelQueries.length > 0) {
      const currentBoolStr = `((${currentBusinessModelQueries.map(q => q.id).join(' OR ')}))`;
      const currentQueueItem = {
        queue_id: `business_model_current_${Date.now() + 1}`,
        section_id: 'business_model_current',
        section_name: 'Current Company Business Model',
        section_type: 'Must Have',
        section_order: 2,
        topic: 'business_model',
        bool_str: currentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: 'I reviewed your existing workforce, and this is the distribution across different current company business models. Which business model(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Current Company Business Models',
          chart_subtitle: 'Distribution of current company business models in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: true, // Only show items with non-zero percentages
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: currentBusinessModelQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(currentQueueItem);
    }

    // 3. Past Business Model queue item
    if (pastBusinessModelQueries.length > 0) {
      const pastBoolStr = `((${pastBusinessModelQueries.map(q => q.id).join(' OR ')}))`;
      const pastQueueItem = {
        queue_id: `business_model_past_${Date.now() + 2}`,
        section_id: 'business_model_past',
        section_name: 'Past Company Business Model',
        section_type: 'Nice to Have',
        section_order: 3,
        topic: 'business_model',
        bool_str: pastBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: 'I reviewed your existing workforce, and this is the distribution across different past company business models. Which business model(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Past Company Business Models',
          chart_subtitle: 'Distribution of past company business models in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: true, // Only show items with non-zero percentages
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: pastBusinessModelQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(pastQueueItem);
    }

    // Add all queue items to centralized queue
    console.log('🏢 BUSINESS MODEL: Adding', queueItems.length, 'items to centralized queue');
    
    // Check if central discovery queue is properly initialized
    if (!centralDiscoveryQueue.sessionId) {
      console.error('❌ BUSINESS MODEL: Central discovery queue not initialized - no session ID');
      throw new Error('Central discovery queue not initialized - no session ID');
    }
    
    console.log('✅ BUSINESS MODEL: Central discovery queue is initialized with session ID:', centralDiscoveryQueue.sessionId);
    
    for (const queueItem of queueItems) {
      try {
        const addedItem = await centralDiscoveryQueue.addQueueItem(queueItem);
        
        // Calculate items count based on template type
        let itemsCount = 0;
        if (queueItem.template_type === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
          itemsCount = queueItem.template_props.data ? queueItem.template_props.data.length : 0;
        }
        
        console.log('✅ BUSINESS MODEL: Added queue item:', {
          section_id: addedItem.section_id,
          section_name: addedItem.section_name,
          template_type: queueItem.template_type,
          items_count: itemsCount
        });
      } catch (error) {
        console.error('❌ BUSINESS MODEL: Error adding queue item:', queueItem.section_id, error);
      }
    }

    // Force immediate save to backend database
    try {
      await centralDiscoveryQueue.saveToDatabase();
      console.log('✅ BUSINESS MODEL: Successfully synchronized', queueItems.length, 'queue items to backend database');
    } catch (saveError) {
      console.error('❌ BUSINESS MODEL: Error saving queue items to backend:', saveError);
    }

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nSuccessfully populated ${queueItems.length} business model queue items.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    console.log('✅ BUSINESS MODEL: Successfully populated centralized queue with', queueItems.length, 'items');

    // Return success data for backward compatibility
    return {
      businessModelData: {
        totalItems: queueItems.length,
        recent: recentBusinessModelQueries.length,
        current: currentBusinessModelQueries.length,
        past: pastBusinessModelQueries.length
      },
      totalCount: recentBusinessModelQueries.length + currentBusinessModelQueries.length + pastBusinessModelQueries.length,
      aiMatchesResult: {
        businessModelData: {
          queueItemsCreated: queueItems.length,
          categories: queueItems.map(item => item.section_id)
        },
        businessModelTotal: queueItems.length
      }
    };

  } catch (error) {
    console.error('❌ BUSINESS MODEL: Error generating business model data:', error);
    
    // Update thinking accordion with error
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nError generating business model data: ${error.message}`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    throw error;
  }
};